import type { Transaction } from "@powersync/web";
import * as Sentry from "@sentry/capacitor";
import {
  type JournalEntry,
  PowerSyncTransactor,
  type TransactionJournal,
} from "@tanstack/powersync-db-collection";
import { toast } from "sonner";
import { z } from "zod";
import { lateRollbackMessage } from "@/lib/collections/persisted";
import { powerSyncDb } from "@/lib/powersync";
import { AppSchema } from "@/lib/powersync-schema.generated";

// Each write from the moment the host makes it until SQLite commits it, which
// after launch waits for its collection's first read, queued behind the
// collections loading ahead of it (patch note in pnpm-workspace.yaml), so the
// next page can write what a reload cut off.
export const KEY_PREFIX = "superhost.write-journal.";
/**
 * Bumped by every replica clear, from any tab. A replay begun before a clear
 * writes nothing, whoever signs in after it.
 */
const CLEAR_EPOCH_KEY = "superhost.replica.clear-epoch";
/** A week past its last write, a journal's writes are stale. */
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
/** Taken by each replay, so two pages opening together cannot both take one. */
const REPLAY_LOCK = "superhost.write-journal";

const PageJournalSchema = z.object({
  /** The clear epoch it was written under; one from before a clear is void. */
  epoch: z.string().nullable(),
  lastWriteAt: z.number(),
  owner: z.string(),
  startedAt: z.number(),
  transactions: z.array(
    z.object({
      entries: z.array(
        z.union([
          z.object({
            id: z.string(),
            op: z.enum(["insert", "update"]),
            table: z.string(),
            values: z.record(z.string(), z.unknown()),
          }),
          z.object({
            id: z.string(),
            op: z.literal("delete"),
            table: z.string(),
          }),
        ])
      ),
    })
  ),
});
type PageJournal = z.infer<typeof PageJournalSchema>;

/** Each view this bundle syncs, with the columns its schema declares. */
const COLUMNS_BY_VIEW = new Map(
  AppSchema.tables.map((table) => [
    table.viewName,
    new Set(table.columns.map(({ name }) => name)),
  ])
);

let page: { journal: PageJournal; key: string } | null = null;

type RecordWrite = TransactionJournal["record"];

interface Replay {
  owner: string;
  record: RecordWrite;
  /** No clear and no other account since this page's replay began. */
  stillApplies: () => boolean;
}

/**
 * Journal this page's writes for `owner`, and write what an earlier page
 * journaled for them and never committed. Call once the replica is known to
 * be theirs; a page shows one account, so later calls are no-ops.
 */
export function startWriteJournal(
  owner: string,
  replicaOwner: () => string | null
): void {
  // Web Locks and randomUUID exist only in a secure context, which the
  // Android dev server (plain http) is not: that page goes unjournaled.
  if (page !== null || !window.isSecureContext) {
    return;
  }
  const key = `${KEY_PREFIX}${crypto.randomUUID()}`;
  const epoch = readKey(CLEAR_EPOCH_KEY);
  const journal: PageJournal = {
    epoch,
    lastWriteAt: Date.now(),
    owner,
    startedAt: Date.now(),
    transactions: [],
  };
  const current = { journal, key };
  page = current;
  // Held for the page's life: another tab's replay must never take this
  // page's writes, which may still commit, for a dead page's.
  void navigator.locks.request(key, () => new Promise<never>(() => undefined));
  const record: RecordWrite = (entries) => {
    // JSON drops an undefined value, which the original write binds as NULL.
    const transaction = {
      entries: entries.map((entry) =>
        entry.op === "delete"
          ? entry
          : {
              ...entry,
              values: Object.fromEntries(
                Object.entries(entry.values).map(([column, value]) => [
                  column,
                  value ?? null,
                ])
              ),
            }
      ),
    };
    followClears(journal);
    journal.transactions.push(transaction);
    journal.lastWriteAt = Date.now();
    store(current);
    return () => {
      followClears(journal);
      journal.transactions = journal.transactions.filter(
        (t) => t !== transaction
      );
      store(current);
    };
  };
  PowerSyncTransactor.journal = { record };
  const replay = {
    owner,
    record,
    stillApplies: () =>
      readKey(CLEAR_EPOCH_KEY) === epoch && replicaOwner() === owner,
  };
  void navigator.locks.request(REPLAY_LOCK, () =>
    replayEarlierPages(key, replay)
  );
}

/**
 * Journal a write that goes to SQLite directly rather than through a
 * collection, for as long as `write` takes.
 */
export async function journaled<T>(
  entries: JournalEntry[],
  write: () => Promise<T>
): Promise<T> {
  const settle = PowerSyncTransactor.journal?.record(entries);
  try {
    return await write();
  } finally {
    settle?.();
  }
}

/** Row writes this page holds that SQLite has not committed yet. */
export function journaledWriteCount(): number {
  if (!page) {
    return 0;
  }
  followClears(page.journal);
  return page.journal.transactions.reduce((n, t) => n + t.entries.length, 0);
}

/**
 * A clear in any tab voids what this page held before it; what the page
 * writes after it is journaled under the new epoch.
 */
function followClears(journal: PageJournal): void {
  const epoch = readKey(CLEAR_EPOCH_KEY);
  if (journal.epoch !== epoch) {
    journal.epoch = epoch;
    journal.transactions = [];
  }
}

/**
 * Drop every page's entries, and any replay not yet landed, for a replica
 * about to be cleared: none of it may land in what the clear leaves.
 */
export function discardWriteJournal(): void {
  for (const key of journalKeys()) {
    removeKey(key);
  }
  try {
    window.localStorage.setItem(CLEAR_EPOCH_KEY, crypto.randomUUID());
  } catch (error) {
    Sentry.captureException(error, { tags: { area: "write-journal" } });
  }
  if (page) {
    page.journal.transactions = [];
    followClears(page.journal);
  }
}

/**
 * Replay every other page's journal: a closed page's now, one still open in
 * another tab once it closes and frees its lock.
 */
async function replayEarlierPages(
  ownKey: string,
  replay: Replay
): Promise<void> {
  // A page whose own lock is still being granted is open too.
  const { held = [], pending = [] } = await navigator.locks.query();
  const open = new Set([...held, ...pending].map(({ name }) => name));
  const others = journalKeys().filter((key) => key !== ownKey);
  for (const key of others.filter((k) => open.has(k))) {
    void navigator.locks.request(key, () =>
      navigator.locks.request(REPLAY_LOCK, () => replayKeys([key], replay))
    );
  }
  await replayKeys(
    others.filter((key) => !open.has(key)),
    replay
  );
}

/**
 * Write what `keys` journaled, in one transaction, page by page in the order
 * they were opened, then forget it whether or not it landed: an entry kept
 * for a later launch would land on top of whatever this page writes to the
 * same row meanwhile. Until it lands it is in this page's own journal, so a
 * reload while it waits for the database loses nothing.
 */
async function replayKeys(
  keys: string[],
  { owner, record, stillApplies }: Replay
): Promise<void> {
  if (keys.length === 0 || !stillApplies()) {
    return;
  }
  // A key can be gone by now: its page committed everything before closing.
  const stored = keys.flatMap((key) => {
    const raw = readKey(key);
    return raw === null ? [] : [parsePage(raw)];
  });
  const readable = stored.filter((j): j is PageJournal => j !== null);
  // One from before a clear was discarded with it, as the host was told.
  const epoch = readKey(CLEAR_EPOCH_KEY);
  const current = readable.filter((j) => j.epoch === epoch);
  const oldest = Date.now() - MAX_AGE_MS;
  const owned = current.filter((j) => j.owner === owner);
  const mine = owned
    .filter((j) => j.lastWriteAt >= oldest)
    .sort((a, b) => a.startedAt - b.startedAt);
  const entries = mine.flatMap((j) => j.transactions.flatMap((t) => t.entries));
  const settle = entries.length > 0 ? record(entries) : undefined;
  for (const key of keys) {
    removeKey(key);
  }
  const expired = owned.length - mine.length;
  const unreplayable =
    stored.length - readable.length + current.length - owned.length;
  if (expired > 0 || unreplayable > 0) {
    Sentry.captureMessage("[write-journal] dropped journals", {
      extra: { expired, unreplayable },
      level: "warning",
    });
  }
  if (!settle) {
    return;
  }
  try {
    await powerSyncDb.writeTransaction(async (tx) => {
      // Asked again here: another tab can clear the replica or sign in
      // another account while this replay waits for the database.
      if (!stillApplies()) {
        Sentry.captureMessage("[write-journal] replay outlived its replica", {
          extra: { dropped: entries.length },
          level: "warning",
        });
        return;
      }
      for (const entry of entries) {
        // biome-ignore lint/performance/noAwaitInLoops: one transaction, applied in the order the host made the writes.
        await replayEntry(tx, entry);
      }
    });
  } catch (error) {
    Sentry.captureException(error, {
      extra: { dropped: entries.length },
      tags: { area: "write-journal" },
    });
    const message = lateRollbackMessage();
    toast.error(message, { id: message });
  } finally {
    settle();
  }
}

/**
 * Apply one entry as the transactor would have, made safe to run twice: a
 * page that reloads between the commit and its journal settling replays a
 * write that already landed. An update finds no row the way the original
 * would have, and does nothing.
 */
async function replayEntry(tx: Transaction, entry: JournalEntry) {
  // Only what this bundle's schema has: an older bundle may have journaled a
  // table or a column that has since been dropped.
  const columns = COLUMNS_BY_VIEW.get(entry.table);
  if (!columns) {
    return;
  }
  const table = `"${entry.table}"`;
  if (entry.op === "delete") {
    await tx.execute(`DELETE FROM ${table} WHERE id = ?`, [entry.id]);
    return;
  }
  const values = Object.entries(entry.values).filter(([column]) =>
    columns.has(column)
  );
  const exists = await tx.getOptional(`SELECT 1 FROM ${table} WHERE id = ?`, [
    entry.id,
  ]);
  if (exists && values.length > 0) {
    await tx.execute(
      `UPDATE ${table} SET ${values.map(([column]) => `"${column}" = ?`).join(", ")} WHERE id = ?`,
      [...values.map(([, value]) => value), entry.id]
    );
  } else if (!exists && entry.op === "insert") {
    await tx.execute(
      `INSERT INTO ${table} (id${values.map(([column]) => `, "${column}"`).join("")}) VALUES (?${", ?".repeat(values.length)})`,
      [entry.id, ...values.map(([, value]) => value)]
    );
  }
}

function journalKeys(): string[] {
  try {
    return Object.keys(window.localStorage).filter((key) =>
      key.startsWith(KEY_PREFIX)
    );
  } catch {
    return [];
  }
}

function readKey(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function parsePage(raw: string): PageJournal | null {
  try {
    const parsed = PageJournalSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/**
 * A journal that cannot be stored (storage full or unavailable) leaves the
 * write exactly as durable as it was without one, so it is reported rather
 * than allowed to fail the write.
 */
function store({ journal, key }: { journal: PageJournal; key: string }) {
  if (journal.transactions.length === 0) {
    removeKey(key);
    return;
  }
  try {
    window.localStorage.setItem(key, JSON.stringify(journal));
  } catch (error) {
    // Never leave an older snapshot behind: it would replay writes that
    // have since committed over newer ones.
    removeKey(key);
    Sentry.captureException(error, { tags: { area: "write-journal" } });
  }
}

function removeKey(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // localStorage unavailable: nothing was journaled either.
  }
}
